import styled from '@emotion/native';
import type { ColorName } from '@emotion/react';
import { Row } from 'components/base';
import type { PrimitiveValueType, SelectOption } from 'components/types';
import {
  Button,
  IconBlock,
  type IconName,
  IconText,
  Text,
  Visible,
} from 'design-system/base';
import { BORDER_WIDTH, MARGIN } from 'design-system/theme';
import { noOp } from 'helpers';
import type React from 'react';
import { TouchableWithoutFeedback } from 'react-native';

import { ComboBoxSingle } from '../input';

export interface HeaderProps {
  title: string;
  icon?: IconName;
  /**
   * To use a text block instead of an icon
   */
  iconText?: string;
  iconColor?: ColorName;
  blockColor?: ColorName;
  borderColor?: ColorName;

  primaryButtonIcon?: IconName;
  primaryButtonText?: string;
  showPrimaryButton?: boolean;
  onPrimaryButtonPress?: () => void;

  secondaryButtonIcon?: IconName;
  secondaryButtonText?: string;
  showSecondaryButton?: boolean;
  onSecondaryButtonPress?: () => void;

  showFilter?: boolean;
  showSecondaryFilter?: boolean;
  filterLabel?: string;
  filterValue?: string;
  filterOptions?: SelectOption[];
  onFilterChange?: (value: PrimitiveValueType) => void;
  onFilterSecondaryChange?: (value: PrimitiveValueType) => void;
  filterSecondaryValue?: string;
  filterSecondaryLabel?: string;
  filterSecondaryOptions?: SelectOption[];

  isSmall?: boolean;
}
interface WrapperProps {
  borderColor?: ColorName;
}

const Wrapper = styled(Row)<WrapperProps>(({ borderColor, theme }) => ({
  alignItems: 'center',
  borderStyle: 'solid',
  borderColor: borderColor || theme.color.divider,
  borderTopWidth: BORDER_WIDTH[0],
  marginTop: MARGIN.none,
}));

const TextWrapper = styled(Row)({
  flex: 1,
  marginLeft: MARGIN.s,
});

const FilterWrapper = styled(Row)({
  flex: 0.7,
});

export const Header: React.FC<HeaderProps> = ({
  blockColor,
  icon,
  iconText,
  iconColor = 'text-on-color',
  title,
  primaryButtonIcon = 'arrow-forward',
  showPrimaryButton = false,
  onPrimaryButtonPress,
  secondaryButtonText,
  secondaryButtonIcon,
  showSecondaryButton = false,
  onSecondaryButtonPress,
  filterOptions = [],
  filterValue,
  onFilterChange = noOp,
  filterLabel,
  showFilter,
  borderColor,
  onFilterSecondaryChange = noOp,
  showSecondaryFilter,
  filterSecondaryOptions = [],
  filterSecondaryValue,
  filterSecondaryLabel,
  isSmall,
}) => (
  <TouchableWithoutFeedback onPress={onPrimaryButtonPress}>
    <Wrapper borderColor={borderColor} unsetZIndex>
      {icon ? (
        <IconBlock name={icon} iconColor={iconColor} blockColor={blockColor} />
      ) : null}
      {iconText && !icon ? (
        <IconText
          text={iconText}
          iconColor={iconColor}
          blockColor={blockColor}
        />
      ) : null}
      <TextWrapper>
        <Text variant={isSmall ? 'body' : 'heading'}>{title}</Text>
      </TextWrapper>
      <Visible if={showSecondaryButton}>
        {onSecondaryButtonPress ? (
          <Button
            variant="flat"
            onPress={onSecondaryButtonPress}
            iconPosition="right"
            icon={secondaryButtonIcon}
            text={secondaryButtonText}
          />
        ) : null}
      </Visible>
      <Visible if={showSecondaryFilter}>
        <FilterWrapper unsetZIndex>
          <ComboBoxSingle
            onChange={onFilterSecondaryChange}
            fetcher={filterSecondaryOptions}
            label={filterSecondaryLabel}
            value={filterSecondaryValue}
          />
        </FilterWrapper>
      </Visible>
      <Visible if={showFilter}>
        <FilterWrapper unsetZIndex>
          <ComboBoxSingle
            onChange={onFilterChange}
            fetcher={filterOptions}
            label={filterLabel}
            value={filterValue}
          />
        </FilterWrapper>
      </Visible>
      <Visible if={showPrimaryButton}>
        {onPrimaryButtonPress ? (
          <Button
            variant="flat"
            onPress={onPrimaryButtonPress}
            iconPosition="right"
            icon={primaryButtonIcon}
          />
        ) : null}
      </Visible>
    </Wrapper>
  </TouchableWithoutFeedback>
);
